<template>
  <div class="sf-el file-upload-input">
    <div class="sf-file-upload" :class="{ 'sf-file-upload--multiple': multiple }">
      <label class="sf-el__label">
        {{ label }}
        <span v-if="required" class="sf-el__required">*</span>
      </label>
      <p v-if="caption" class="sf-el__caption">{{ caption }}</p>

      <div class="sf-file-upload__container">

        <!-- ── SINGLE MODE (default behaviour, unchanged) ── -->
        <template v-if="!multiple">
          <div v-if="previewUrl && isImage" class="sf-file-upload__preview">
            <img :src="previewUrl" alt="Preview" class="sf-file-upload__image" />
            <div class="sf-file-upload__actions">
              <button
                type="button"
                @click="changeFile"
                class="sf-file-upload__btn sf-file-upload__btn--change"
              >
                <BaseIcon name="edit" size="16" />
                Change
              </button>
              <button
                type="button"
                @click="removeFile"
                class="sf-file-upload__btn sf-file-upload__btn--remove"
              >
                <BaseIcon name="trash" size="16" />
                Remove
              </button>
            </div>
          </div>

          <div v-else-if="previewUrl && !isImage" class="sf-file-upload__file-info">
            <div class="sf-file-upload__file">
              <BaseIcon name="file" size="24" />
              <span class="sf-file-upload__filename">{{ fileName }}</span>
            </div>
            <button
              type="button"
              @click="removeFile"
              class="sf-file-upload__btn sf-file-upload__btn--remove"
            >
              <BaseIcon name="trash" size="16" />
              Remove
            </button>
          </div>

          <div v-else class="sf-file-upload__upload">
            <div class="sf-file-upload__dropzone" @click="handleDropzoneClick">
              <div class="sf-file-upload__dropzone-icon">
                <BaseIcon name="upload" size="18" />
              </div>
              <p class="sf-file-upload__text">{{ uploadText || 'Click to upload file' }}</p>
              <p v-if="acceptedTypes" class="sf-file-upload__types">{{ acceptedTypes }}</p>
            </div>
          </div>
        </template>

        <!-- ── MULTIPLE MODE ── -->
        <template v-else>
          <!-- Uploaded files grid -->
          <div v-if="multipleFiles.length" class="sf-file-upload__multi-grid">
            <div
              v-for="(file, idx) in multipleFiles"
              :key="file.url || idx"
              class="sf-file-upload__multi-item"
            >
              <img
                v-if="isImageUrl(file.url)"
                :src="file.url"
                :alt="file.fileName"
                class="sf-file-upload__multi-thumb"
              />
              <div v-else class="sf-file-upload__multi-file">
                <BaseIcon name="file" size="20" />
                <span class="sf-file-upload__multi-file-name">{{ file.fileName }}</span>
              </div>
              <button
                type="button"
                class="sf-file-upload__multi-remove"
                @click="removeMultiFile(idx)"
              >
                <BaseIcon name="xmark" size="12" />
              </button>
            </div>
          </div>

          <!-- Always-visible dropzone to add more -->
          <div
            class="sf-file-upload__dropzone"
            :class="{ 'sf-file-upload__dropzone--compact': multipleFiles.length > 0 }"
            @click="handleDropzoneClick"
          >
            <div class="sf-file-upload__dropzone-icon">
              <BaseIcon name="upload" size="18" />
            </div>
            <p class="sf-file-upload__text">
              {{ multipleFiles.length > 0 ? 'Nog een bestand toevoegen' : (uploadText || 'Click to upload file') }}
            </p>
            <p v-if="acceptedTypes && multipleFiles.length === 0" class="sf-file-upload__types">
              {{ acceptedTypes }}
            </p>
          </div>
        </template>

        <!-- Loading state (shared) -->
        <div v-if="isUploading" class="sf-file-upload__loading">
          <div class="sf-file-upload__spinner"></div>
          <span>Uploading...</span>
        </div>

        <!-- Error state (shared) -->
        <div v-if="uploadError" class="sf-file-upload__error">
          <BaseIcon name="alert-circle" size="16" />
          {{ uploadError }}
        </div>
      </div>

      <!-- Native OS picker handles camera vs gallery selection on mobile -->
      <input
        ref="fileInput"
        type="file"
        :accept="accept"
        :multiple="multiple"
        @change="onFileChange"
        style="display: none"
      />
    </div>
  </div>
</template>

<script>
  const getFileUploadService = async () => {
    const { fileUploadService } = await import('@shared/services/fileUploadService');
    return fileUploadService;
  };

  const IMAGE_EXTENSIONS = /\.(jpeg|jpg|gif|png|webp)(\?.*)?$/i;

  import { watch, ref, computed, onMounted } from 'vue';
  import BaseIcon from '../../BaseIcon.vue';

  export default {
    name: 'FileUploadElement',
    components: {
      BaseIcon,
    },
    props: {
      label: String,
      description: String,
      caption: String,
      name: String,
      // Single mode: String. Multiple mode: Array of strings.
      modelValue: {
        type: [String, Array],
        default: '',
      },
      required: Boolean,
      // When true the user can upload multiple files; emits String[] instead of String.
      // Defaults to false so existing single-file uploads are unaffected.
      multiple: {
        type: Boolean,
        default: false,
      },
      fieldId: {
        type: String,
        required: true,
      },
      accept: {
        type: String,
        default: '*/*',
      },
      uploadText: String,
      acceptedTypes: String,
      maxFileSize: {
        type: Number,
        default: 10 * 1024 * 1024, // 10 MB
      },
      uploadConfig: {
        type: Object,
        default: () => ({
          type: 'form_file',
          context: 'form',
        }),
      },
      formId: {
        type: String,
        required: true,
      },
    },
    emits: ['update:modelValue'],
    setup(props, { emit }) {
      const fileInput = ref(null);
      const isUploading = ref(false);
      const uploadError = ref('');
      const fileUploadService = ref(null);

      // ── Single-mode state ──────────────────────────────────
      const previewUrl = ref('');
      const fileName = ref('');

      const isImage = computed(() => {
        if (!previewUrl.value) return false;
        if (previewUrl.value.startsWith('blob:')) {
          return IMAGE_EXTENSIONS.test(fileName.value);
        }
        return IMAGE_EXTENSIONS.test(previewUrl.value);
      });

      // ── Multiple-mode state ────────────────────────────────
      // Each entry: { url: string, fileName: string }
      const multipleFiles = ref([]);

      const isImageUrl = (url) => IMAGE_EXTENSIONS.test(url || '');

      // ── Service init ───────────────────────────────────────
      onMounted(async () => {
        fileUploadService.value = await getFileUploadService();
      });

      // ── Upload helper (shared) ─────────────────────────────
      const uploadSingleFile = async (file) => {
        if (file.size > props.maxFileSize) {
          uploadError.value = `Bestand te groot (max ${Math.round(props.maxFileSize / 1024 / 1024)} MB)`;
          return null;
        }
        const response = await fileUploadService.value.uploadFile(file, {
          ...props.uploadConfig,
          formId: props.formId,
          fieldId: props.fieldId,
        });
        return response || null;
      };

      // ── File input handler ─────────────────────────────────
      const onFileChange = async (event) => {
        if (!fileUploadService.value) return;
        uploadError.value = '';

        if (props.multiple) {
          // Multiple mode: upload each selected file and append to the list
          const files = Array.from(event.target.files);
          if (!files.length) return;

          isUploading.value = true;
          try {
            for (const file of files) {
              const response = await uploadSingleFile(file);
              if (response?.url) {
                multipleFiles.value = [
                  ...multipleFiles.value,
                  { url: response.url, fileName: file.name },
                ];
                emit('update:modelValue', multipleFiles.value.map((f) => f.url));
              }
            }
          } catch (err) {
            console.error('Error uploading file:', err);
            uploadError.value = 'Upload mislukt. Probeer het opnieuw.';
          } finally {
            isUploading.value = false;
            // Reset so the same file can be re-selected if needed
            event.target.value = '';
          }
        } else {
          // Single mode: existing behaviour
          const file = event.target.files[0];
          if (!file) return;

          let tempBlobUrl = null;
          try {
            fileName.value = file.name;
            if (file.type.startsWith('image/')) {
              tempBlobUrl = URL.createObjectURL(file);
              previewUrl.value = tempBlobUrl;
            }

            isUploading.value = true;
            const response = await uploadSingleFile(file);

            if (response?.id) {
              emit('update:modelValue', response.id);
              if (file.type.startsWith('image/') && response.url) {
                if (tempBlobUrl) URL.revokeObjectURL(tempBlobUrl);
                previewUrl.value = response.url;
              } else if (!file.type.startsWith('image/')) {
                previewUrl.value = response.url || response.id;
              }
            }
          } catch (err) {
            console.error('Error uploading file:', err);
            uploadError.value = 'Upload mislukt. Probeer het opnieuw.';
            if (tempBlobUrl) URL.revokeObjectURL(tempBlobUrl);
            removeFile();
          } finally {
            isUploading.value = false;
          }
        }
      };

      // ── Single-mode actions ────────────────────────────────
      const triggerFileInput = () => fileInput.value?.click();
      const changeFile = () => triggerFileInput();

      // The native OS picker (iOS / Android) already shows
      // "Take Photo / Photo Library / Browse" for accept="image/*",
      // so we just open the single hidden input on click.
      const handleDropzoneClick = () => triggerFileInput();

      const removeFile = () => {
        if (previewUrl.value?.startsWith('blob:')) URL.revokeObjectURL(previewUrl.value);
        previewUrl.value = '';
        fileName.value = '';
        uploadError.value = '';
        emit('update:modelValue', '');
      };

      // ── Multiple-mode actions ──────────────────────────────
      const removeMultiFile = (idx) => {
        multipleFiles.value = multipleFiles.value.filter((_, i) => i !== idx);
        emit('update:modelValue', multipleFiles.value.map((f) => f.url));
      };

      // ── Sync incoming modelValue ───────────────────────────
      watch(
        () => props.modelValue,
        (newValue) => {
          if (props.multiple) {
            const urls = Array.isArray(newValue)
              ? newValue
              : newValue
              ? [newValue]
              : [];
            multipleFiles.value = urls.map((url) => ({
              url,
              fileName: url.split('/').pop(),
            }));
          } else {
            if (newValue && !previewUrl.value) {
              previewUrl.value = newValue;
            } else if (!newValue) {
              previewUrl.value = '';
              fileName.value = '';
            }
          }
        },
        { immediate: true }
      );

      return {
        fileInput,
        // single
        previewUrl,
        fileName,
        isImage,
        changeFile,
        removeFile,
        // multiple
        multipleFiles,
        isImageUrl,
        removeMultiFile,
        // shared
        isUploading,
        uploadError,
        triggerFileInput,
        onFileChange,
        handleDropzoneClick,
      };
    },
  };
</script>

<style scoped lang="scss">
  .sf-file-upload {
    &__container {
      position: relative;
    }

    &__dropzone {
      border: 2px dashed var(--cs-input-default-border);
      border-radius: 0.5rem;
      padding: 2rem;
      text-align: center;
      cursor: pointer;
      transition: all 0.2s ease;
      background: var(--cs-input-default-background);

      &:hover {
        border-color: var(--cs-brand-primary);
      }

      &--compact {
        padding: 0.875rem 1rem;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 0.5rem;
        margin-top: 0.5rem;

        .sf-file-upload__dropzone-icon {
          padding: 0.375rem;
        }

        .sf-file-upload__text {
          margin: 0;
          font-size: 0.875rem;
        }
      }

      &-icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        padding: 0.75rem;
        border: 1px solid var(--cs-input-default-border);
        border-radius: 0.5rem;
      }
    }

    &__multi-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
      gap: 0.5rem;
      margin-bottom: 0.5rem;
    }

    &__multi-item {
      position: relative;
      border-radius: 0.375rem;
      overflow: hidden;
      border: 1px solid var(--cs-input-default-border);
      background: var(--cs-input-default-background);
      aspect-ratio: 1;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    &__multi-thumb {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    &__multi-file {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 0.25rem;
      padding: 0.5rem;
      text-align: center;
    }

    &__multi-file-name {
      font-size: 0.6875rem;
      color: var(--cs-input-default-text);
      word-break: break-all;
      line-height: 1.2;
    }

    &__multi-remove {
      position: absolute;
      top: 0.25rem;
      right: 0.25rem;
      width: 1.25rem;
      height: 1.25rem;
      border-radius: 9999px;
      background: rgba(0, 0, 0, 0.55);
      color: white;
      border: none;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: background 0.15s ease;

      &:hover {
        background: rgba(220, 38, 38, 0.85);
      }
    }

    &__text {
      margin: 0.5rem 0 0;
      font-weight: 500;
      color: var(--cs-input-default-text);
    }

    &__types {
      margin: 0.25rem 0 0;
      font-size: 0.875rem;
      color: var(--cs-input-default-text);
    }

    &__preview {
      display: flex;
      flex-direction: column;
      gap: 1rem;
      padding: 1rem;
      border: 1px solid var(--cs-input-default-border);
      border-radius: 0.5rem;
      background: var(--cs-input-default-background);
    }

    &__image {
      max-width: 100%;
      max-height: 200px;
      object-fit: contain;
      border-radius: 0.25rem;
    }

    &__file-info {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 1rem;
      border: 1px solid var(--cs-input-default-border);
      border-radius: 0.5rem;
      background: var(--cs-input-default-background);
    }

    &__file {
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    &__filename {
      font-weight: 500;
      color: var(--cs-input-default-text);
    }

    &__actions {
      display: flex;
      gap: 0.5rem;
      justify-content: center;
    }

    &__btn {
      display: flex;
      align-items: center;
      gap: 0.25rem;
      padding: 0.5rem 1rem;
      border: 1px solid var(--cs-input-default-border);
      border-radius: 0.25rem;
      background: var(--cs-input-default-background);
      color: var(--cs-input-default-text);
      font-size: 0.875rem;
      cursor: pointer;
      transition: all 0.2s ease;

      &:hover {
        background: var(--slate-50);
        border-color: var(--slate-400);
      }

      &--remove {
        color: var(--red-600);
        border-color: var(--red-300);

        &:hover {
          background: var(--red-50);
          border-color: var(--red-400);
        }
      }
    }

    &__loading {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 1rem;
      justify-content: center;
      color: var(--slate-600);
    }

    &__spinner {
      width: 1rem;
      height: 1rem;
      border: 2px solid var(--slate-300);
      border-top: 2px solid var(--cs-brand-primary);
      border-radius: 50%;
      animation: spin 1s linear infinite;
    }

    &__error {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.75rem;
      background: var(--red-50);
      border: 1px solid var(--red-200);
      border-radius: 0.25rem;
      color: var(--red-700);
      font-size: 0.875rem;
      margin-top: 0.5rem;
    }

  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
</style>
